/**
 * Cookbook → grocery list helpers. Wraps the existing
 * `generateGroceryListFromCookbook` callable Cloud Function — the same
 * one iOS calls from `CookbookRepository.generateGroceryList(...)`
 * (`Views/Cookbook/CookbookRepository.swift:69-83`) and Android from
 * its parallel implementation.
 *
 * Web has no separate "selected meals tray" UI yet, so for now the
 * detail sheet calls this with a single meal + chosen servings. The
 * CF accepts an array of `{mealId, servings}` selections so the same
 * helper is forward-compatible if we ever add a tray.
 */

import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

export interface CookbookSelection {
  mealId: string;
  servings: number;
}

export interface GenerateGroceryListResult {
  added?: number;
  ingredients?: number;
  message?: string;
  [key: string]: unknown;
}

/**
 * Calls `generateGroceryListFromCookbook`. Returns the parsed result.
 * Throws on auth / household-membership / unexpected errors so the
 * caller can show a toast.
 */
export async function generateGroceryListFromCookbook(args: {
  householdId: string;
  selections: CookbookSelection[];
  excludedIngredientIds?: string[];
}): Promise<GenerateGroceryListResult> {
  const functions = getFunctions(getApp());
  const fn = httpsCallable<
    {
      householdId: string;
      selections: CookbookSelection[];
      excludedIngredientIds: string[];
    },
    GenerateGroceryListResult
  >(functions, "generateGroceryListFromCookbook");
  const res = await fn({
    householdId: args.householdId,
    selections: args.selections,
    excludedIngredientIds: args.excludedIngredientIds ?? [],
  });
  return res.data ?? {};
}
